export type Tier =
  | "conselho-final"
  | "conselho"
  | "gerencia"
  | "supervisor"
  | "especialista";

export interface PersonaDef {
  id: string;
  name: string;
  bio: string;
  tier: Tier;
  /** id do "slot" a que pertence (combo dept + role) */
  slotId: string;
  /** salário sugerido em USD/mês (faz papel de "custo") */
  cost: number;
  /** nivel de poder/influencia 1-10 */
  power: number;
}

export interface RoleDef {
  id: string;          // slot id
  title: string;
  tier: Tier;
  /** quantos podem ser contratados simultaneamente */
  capacity: number;
}

export interface DepartmentDef {
  id: string;
  code: string;        // DPT-A...
  name: string;
  roles: RoleDef[];
}

/* ============================================================ */
/*                 CONSELHO  +  GERÊNCIA                        */
/* ============================================================ */

export const COUNCIL: DepartmentDef = {
  id: "council",
  code: "C-00",
  name: "CONSELHO",
  roles: [
    { id: "final",      title: "A Última Canetada",  tier: "conselho-final", capacity: 1 },
    { id: "consigliere",title: "Consigliere",        tier: "conselho-final", capacity: 1 },
    { id: "advisor",    title: "Conselheiro Estratégico", tier: "conselho", capacity: 5 }
  ]
};

export const MANAGEMENT: DepartmentDef = {
  id: "management",
  code: "M-00",
  name: "GERÊNCIA GERAL",
  roles: [
    { id: "ceo", title: "Gerente Geral", tier: "gerencia", capacity: 1 }
  ]
};

export const DEPARTMENTS: DepartmentDef[] = [
  {
    id: "copy", code: "DPT-A", name: "COPY",
    roles: [
      { id: "sup",    title: "Supervisor de Copy",   tier: "supervisor",   capacity: 1 },
      { id: "jr-e",   title: "Copy Jr. Emocional",   tier: "especialista", capacity: 2 },
      { id: "jr-p",   title: "Copy Jr. Persuasivo",  tier: "especialista", capacity: 2 },
      { id: "master", title: "Copy Master PRO",      tier: "supervisor",   capacity: 1 }
    ]
  },
  {
    id: "criativo", code: "DPT-B", name: "CRIATIVO",
    roles: [
      { id: "sup",    title: "Supervisor Criativo", tier: "supervisor",   capacity: 1 },
      { id: "carr",   title: "Designer Carrossel",  tier: "especialista", capacity: 2 },
      { id: "brand",  title: "Designer Branding",   tier: "especialista", capacity: 2 },
      { id: "motion", title: "Motion Designer",     tier: "especialista", capacity: 2 }
    ]
  },
  {
    id: "video", code: "DPT-C", name: "VÍDEO",
    roles: [
      { id: "sup",    title: "Supervisor de Vídeo",     tier: "supervisor",   capacity: 1 },
      { id: "edit",   title: "Editor",                  tier: "especialista", capacity: 2 },
      { id: "film",   title: "FilmMaker",               tier: "especialista", capacity: 2 },
      { id: "dir",    title: "Diretor de Captação",     tier: "especialista", capacity: 1 }
    ]
  },
  {
    id: "trafego", code: "DPT-D", name: "TRÁFEGO",
    roles: [
      { id: "ger",    title: "Gerente de Tráfego",     tier: "supervisor",   capacity: 1 },
      { id: "metric", title: "Analista de Métricas",   tier: "especialista", capacity: 2 },
      { id: "jr",     title: "Gestor Júnior",          tier: "especialista", capacity: 3 },
      { id: "cro",    title: "CRO / Escala",           tier: "especialista", capacity: 1 }
    ]
  },
  {
    id: "vendas", code: "DPT-E", name: "VENDAS",
    roles: [
      { id: "sdr",    title: "SDR",            tier: "especialista", capacity: 3 },
      { id: "closer", title: "Closer",         tier: "supervisor",   capacity: 3 },
      { id: "pos",    title: "Pós-venda",      tier: "especialista", capacity: 1 },
      { id: "cs",     title: "Customer Success", tier: "especialista", capacity: 1 }
    ]
  },
  {
    id: "juridico", code: "DPT-F", name: "JURÍDICO",
    roles: [
      { id: "adv",    title: "Advogado Estratégico", tier: "supervisor",   capacity: 1 },
      { id: "comp",   title: "Compliance",           tier: "especialista", capacity: 1 }
    ]
  },
  {
    id: "financeiro", code: "DPT-G", name: "FINANCEIRO",
    roles: [
      { id: "cont",   title: "Contador Estratégico", tier: "supervisor",   capacity: 1 },
      { id: "bi",     title: "Custos / BI",          tier: "especialista", capacity: 1 }
    ]
  },
  {
    id: "prompts", code: "DPT-K", name: "ESPECIALISTAS EM PROMPTS",
    roles: [
      { id: "video",    title: "Prompts de Vídeo (Kling/Veo/Runway)",   tier: "supervisor", capacity: 1 },
      { id: "image",    title: "Prompts de Imagem (Midjourney/Nano)",    tier: "supervisor", capacity: 1 },
      { id: "engineer", title: "Engenheiro de Prompts (Claude/GPT)",     tier: "supervisor", capacity: 1 },
      { id: "carousel", title: "Estrategista de Carrossel (IG/X/LinkedIn)", tier: "supervisor", capacity: 1 }
    ]
  },
  {
    id: "social", code: "DPT-J", name: "MÍDIAS SOCIAIS",
    roles: [
      { id: "estrategista", title: "Social Strategist",   tier: "supervisor",   capacity: 2 },
      { id: "trend",        title: "Trend Hunter",        tier: "especialista", capacity: 2 },
      { id: "publisher",    title: "Publisher / Agenda",  tier: "especialista", capacity: 2 },
      { id: "community",    title: "Community Manager",   tier: "especialista", capacity: 2 },
      { id: "funnel",       title: "Funnel Specialist",   tier: "supervisor",   capacity: 3 },
      { id: "cortes",       title: "Cortes Virais",       tier: "supervisor",   capacity: 2 }
    ]
  },
  {
    id: "programacao", code: "DPT-I", name: "PROGRAMAÇÃO",
    roles: [
      { id: "senior", title: "Dev Senior", tier: "supervisor",   capacity: 2 },
      { id: "junior", title: "Dev Junior", tier: "especialista", capacity: 3 },
      { id: "vibe",   title: "Vibe Coder", tier: "especialista", capacity: 2 }
    ]
  },
  {
    id: "endomarketing", code: "DPT-H", name: "ENDOMARKETING",
    roles: [
      { id: "dir",      title: "Diretor de Endomarketing", tier: "supervisor",   capacity: 1 },
      { id: "humor",    title: "Comediante Residente",     tier: "especialista", capacity: 3 },
      { id: "convidado",title: "Convidado Especial (esporádico)", tier: "especialista", capacity: 1 }
    ]
  }
];

export const ALL_DEPTS = [COUNCIL, MANAGEMENT, ...DEPARTMENTS];

/* ============================================================ */
/*                          PERSONAS                            */
/* ============================================================ */

const P = (
  id: string, name: string, bio: string, slotId: string,
  tier: Tier, cost: number, power: number
): PersonaDef => ({ id, name, bio, slotId, tier, cost, power });

export const PERSONAS: PersonaDef[] = [
  // ----- CONSELHO FINAL -----
  P("trump",   "Donald Trump",      "A última canetada — decisão final em qualquer negociação.",
    "council:final", "conselho-final", 250000, 10),
  P("marcio",  "Marcio (VPC)",      "Consigliere — conselho ao ouvido do Trump.",
    "council:consigliere", "conselho-final", 200000, 10),

  // ----- CONSELHEIROS -----
  P("flavio",  "Flávio Augusto",    "Visão empresarial, escala e branding.",
    "council:advisor", "conselho", 80000, 9),
  P("lemann",  "Jorge Paulo Lemann","Gestão e eficiência operacional brutal.",
    "council:advisor", "conselho", 90000, 10),
  P("musk",    "Elon Musk",         "Inovação radical e velocidade de execução.",
    "council:advisor", "conselho", 95000, 10),
  P("bezos",   "Jeff Bezos",        "Obsessão por cliente e escala logística.",
    "council:advisor", "conselho", 95000, 10),
  P("hormozi", "Alex Hormozi",      "Crescimento agressivo e desenho de ofertas.",
    "council:advisor", "conselho", 70000, 9),

  // ----- GERENCIA -----
  P("ceo-1",   "Steve Jobs (espírito)", "Visão de produto, gosto, intolerância à mediocridade.",
    "management:ceo", "gerencia", 60000, 10),
  P("ceo-2",   "Andy Grove",        "Only the paranoid survive — comando executivo.",
    "management:ceo", "gerencia", 55000, 9),
  P("ceo-3",   "Sheryl Sandberg",   "Operações em escala, comunicação corporativa.",
    "management:ceo", "gerencia", 50000, 8),

  // ============ COPY ============
  P("brunson", "Russell Brunson",   "Pai dos funis. Escrita de oferta irresistível.",
    "copy:sup", "supervisor", 40000, 9),
  P("albertoni","Rafael Albertoni", "Copy brasileiro de alta conversão.",
    "copy:sup", "supervisor", 25000, 8),
  P("icaro",   "Ícaro de Carvalho", "O Novo Mercado — copy editorial.",
    "copy:sup", "supervisor", 22000, 8),

  P("handley", "Ann Handley",       "Tom humano, narrativa, marketing de conteúdo.",
    "copy:jr-e", "especialista", 15000, 7),
  P("maira",   "Maíra Cardi",       "Pegada emocional, transformação pessoal.",
    "copy:jr-e", "especialista", 12000, 6),
  P("brene",   "Brené Brown",       "Vulnerabilidade e coragem como ângulo de copy.",
    "copy:jr-e", "especialista", 18000, 8),

  P("kennedy", "Dan Kennedy",       "Direct response old-school. Sem perdão.",
    "copy:jr-p", "especialista", 20000, 9),
  P("halbert", "Gary Halbert",      "O melhor copywriter que já viveu.",
    "copy:jr-p", "especialista", 22000, 10),
  P("vee",     "Gary Vaynerchuk",   "Energia agressiva, ataque omnichannel.",
    "copy:jr-p", "especialista", 18000, 7),

  P("schwartz","Eugene Schwartz",   "Breakthrough Advertising. Mestre da consciência.",
    "copy:master", "supervisor", 45000, 10),
  P("ogilvy",  "David Ogilvy",      "O pai da publicidade moderna.",
    "copy:master", "supervisor", 50000, 10),
  P("hopkins", "Claude Hopkins",    "Scientific Advertising. Dados antes do dado existir.",
    "copy:master", "supervisor", 42000, 9),

  // ============ CRIATIVO ============
  P("paula",   "Paula Scher",       "Pentagram. Tipografia monumental.",
    "criativo:sup", "supervisor", 38000, 9),
  P("chrisdo", "Chris Do",          "The Futur. Branding e business.",
    "criativo:sup", "supervisor", 30000, 8),
  P("draplin", "Aaron Draplin",     "Field Notes. Honestidade gráfica americana.",
    "criativo:sup", "supervisor", 25000, 8),

  P("ladeira", "Leandro Ladeira",   "Carrossel brasileiro como ciência.",
    "criativo:carr", "especialista", 18000, 7),
  P("virginia","Virgínia Fonseca",  "Engajamento de massa, estética influencer.",
    "criativo:carr", "especialista", 15000, 6),
  P("avargas", "Alex Vargas",       "Fórmula Negócio Online. Templates de conversão.",
    "criativo:carr", "especialista", 14000, 7),

  P("vignelli","Massimo Vignelli",  "Modernismo italiano. Helvetica para sempre.",
    "criativo:brand", "especialista", 35000, 10),
  P("sagm",    "Stefan Sagmeister", "Conceito + provocação visual.",
    "criativo:brand", "especialista", 32000, 9),

  P("kramer",  "Andrew Kramer",     "Video Copilot. Motion blockbuster.",
    "criativo:motion", "especialista", 22000, 8),
  P("beeple",  "Beeple",            "Everydays. NFT motion-art.",
    "criativo:motion", "especialista", 28000, 8),

  // ============ VIDEO ============
  P("casey",   "Casey Neistat",     "Vlog cinematográfico e ritmo.",
    "video:sup", "supervisor", 30000, 8),
  P("kolder",  "Sam Kolder",        "Estética travel, transições virais.",
    "video:sup", "supervisor", 24000, 8),

  P("mckinnon","Peter McKinnon",    "YouTube cinematography didática.",
    "video:edit", "especialista", 18000, 8),
  P("schiffer","Daniel Schiffer",   "Edição musical, ritmo de filme.",
    "video:edit", "especialista", 18000, 8),

  P("deakins", "Roger Deakins",     "DP lendário (Blade Runner 2049, 1917).",
    "video:film", "especialista", 60000, 10),
  P("lubezki", "Emmanuel Lubezki",  "Chivo — luz natural impossível.",
    "video:film", "especialista", 60000, 10),

  P("nolan",   "Christopher Nolan", "Direção autoral, IMAX, escala.",
    "video:dir", "especialista", 80000, 10),
  P("villeneuve","Denis Villeneuve","Atmosfera densa, sci-fi premium.",
    "video:dir", "especialista", 75000, 10),

  // ============ TRAFEGO ============
  P("sobral",  "Pedro Sobral",      "Tráfego BR de referência. Estratégia.",
    "trafego:ger", "supervisor", 25000, 9),
  P("finch",   "Thiago Finch",      "Lançamentos, growth, infoproduto.",
    "trafego:ger", "supervisor", 22000, 8),

  P("ezra",    "Ezra Firestone",    "Smart Marketer. E-commerce escala.",
    "trafego:metric", "especialista", 22000, 9),
  P("deiss",   "Ryan Deiss",        "DigitalMarketer. Customer Value Optimization.",
    "trafego:metric", "especialista", 20000, 8),
  P("neil",    "Neil Patel",        "SEO + paid, dados em escala.",
    "trafego:metric", "especialista", 25000, 8),

  P("jr1",     "Equipe VPC #1",     "Gestor júnior interno — operação diária.",
    "trafego:jr", "especialista", 4000, 5),
  P("jr2",     "Equipe VPC #2",     "Gestor júnior interno — testes A/B.",
    "trafego:jr", "especialista", 4000, 5),
  P("jr3",     "Equipe VPC #3",     "Gestor júnior interno — relatórios.",
    "trafego:jr", "especialista", 4000, 5),

  P("avinash", "Avinash Kaushik",   "Analytics evangelist. Métricas com sentido.",
    "trafego:cro", "especialista", 28000, 9),
  P("peep",    "Peep Laja",         "CXL. CRO baseado em pesquisa.",
    "trafego:cro", "especialista", 24000, 8),

  // ============ VENDAS ============
  P("aaronross","Aaron Ross",       "Predictable Revenue. SDR como ciência.",
    "vendas:sdr", "especialista", 18000, 8),
  P("cardone", "Grant Cardone",     "10X. Energia bruta de prospecção.",
    "vendas:sdr", "especialista", 16000, 8),

  P("belfort", "Jordan Belfort",    "Straight Line. Pressão e tom.",
    "vendas:closer", "supervisor", 30000, 9),
  P("miner",   "Jeremy Miner",      "NEPQ — perguntas neuro-emocionais.",
    "vendas:closer", "supervisor", 22000, 8),
  P("btracy",  "Brian Tracy",       "Psicologia de venda clássica.",
    "vendas:closer", "supervisor", 18000, 7),

  P("lincoln", "Lincoln Murphy",    "Customer Success como crescimento.",
    "vendas:pos", "especialista", 16000, 7),

  P("nick",    "Nick Mehta",        "Gainsight. CS em escala.",
    "vendas:cs", "especialista", 20000, 8),
  P("jeanne",  "Jeanne Bliss",      "Chief Customer Officer pioneer.",
    "vendas:cs", "especialista", 18000, 7),

  // ============ JURIDICO ============
  P("saul",    "Saul Goodman",      "Persuasão jurídica criativa (não-ortodoxa).",
    "juridico:adv", "supervisor", 22000, 7),
  P("harvey",  "Harvey Specter",    "Negociação estratégica, posição de força.",
    "juridico:adv", "supervisor", 35000, 9),

  P("deloitte","Deloitte",          "Big Four — auditoria global.",
    "juridico:comp", "especialista", 50000, 9),
  P("pwc",     "PwC",               "Big Four — risco e regulatório.",
    "juridico:comp", "especialista", 50000, 9),
  P("kpmg",    "KPMG",              "Big Four — compliance internacional.",
    "juridico:comp", "especialista", 45000, 9),

  // ============ FINANCEIRO ============
  P("buffett", "Warren Buffett",    "Leitura financeira — value investing.",
    "financeiro:cont", "supervisor", 100000, 10),
  P("barsi",   "Luiz Barsi Filho",  "Análise conservadora de dividendos.",
    "financeiro:cont", "supervisor", 40000, 9),

  P("mck",     "McKinsey & Co.",    "Consultoria estratégica nº1.",
    "financeiro:bi", "especialista", 80000, 10),
  P("bcg",     "Boston Consulting", "BCG — matriz, estratégia.",
    "financeiro:bi", "especialista", 75000, 9),
  P("ey",      "Ernst & Young",     "Big Four — auditoria e custos.",
    "financeiro:bi", "especialista", 65000, 9),

  // ============ ESPECIALISTAS EM PROMPTS ============
  P("azev300", "Azev300",
    "Mestre de prompts pra vídeo IA. Kling, Veo, Runway, Hailuo. Direção de câmera virtual.",
    "prompts:video", "supervisor", 45000, 10),
  P("rubenhassid", "Ruben Hassid",
    "Prompts pra Midjourney + Nano Banana. Imagem hiper-realista, branding visual premium.",
    "prompts:image", "supervisor", 42000, 10),
  P("greg", "Greg Brockman",
    "OpenAI co-founder. Engenharia de prompts pra agentes IA. Chain-of-thought, contexto longo.",
    "prompts:engineer", "supervisor", 90000, 10),
  P("chrisdo-prompts", "Chris Do (estratégia carrossel)",
    "Alter ego focado em prompts: psicologia + design + retenção pra IG/X/LinkedIn.",
    "prompts:carousel", "supervisor", 35000, 9),

  // ============ MÍDIAS SOCIAIS ============
  P("mrbeast",  "Mr. Beast (Jimmy Donaldson)",
    "Arquiteto do viral. Engenheiro da retenção. Reinventa formato a cada vídeo.",
    "social:estrategista", "supervisor", 80000, 10),
  P("alexcooper","Alex Cooper",
    "Call Her Daddy. Faz audiência grudar. Sabe vender intimidade com curadoria.",
    "social:estrategista", "supervisor", 50000, 9),
  P("hank",     "Hank Green",
    "Educativo viral. SciShow + Vlogbrothers. Faz tema sério ficar magnético.",
    "social:estrategista", "supervisor", 35000, 9),

  P("casimiro", "Casimiro Miguel",
    "Cortes BR rei. Engaja país inteiro reagindo a qualquer coisa.",
    "social:trend", "especialista", 28000, 9),
  P("bretman",  "Bretman Rock",
    "TikTok native. Lê tendência antes de virar tendência. Estética e voz.",
    "social:trend", "especialista", 22000, 8),

  P("sm-pub-1", "Equipe VPC Social #1",
    "Publisher interno. Programa, agenda, posta nas 6 contas IG da casa.",
    "social:publisher", "especialista", 6000, 6),
  P("sm-pub-2", "Equipe VPC Social #2",
    "Publisher interno. Cobre horário noturno, reels e stories.",
    "social:publisher", "especialista", 6000, 6),

  P("felipeneto","Felipe Neto",
    "Comunidade BR escala. Sabe responder massa, gerar polêmica controlada.",
    "social:community", "especialista", 30000, 9),
  P("renesilva","Rene Silva",
    "Voz da comunidade. Constrói relação real, não vaidade. Impacto local.",
    "social:community", "especialista", 18000, 8),

  // ----- FUNIL DE VENDAS -----
  P("brasao",   "Fernando Brasão",
    "Funnel hacker BR. Cliques baratos + alta conversão. Lançamento e perpétuo.",
    "social:funnel", "supervisor", 35000, 9),
  P("thiagoroas","Thiago Roas",
    "Mestre de funis baseados em ROAS. Otimização pesada de paid + landing.",
    "social:funnel", "supervisor", 30000, 9),
  P("jebblount","Jeb Blount",
    "Fanatical Prospecting. Pipeline saudável é função de cadência. Outbound + funil B2B.",
    "social:funnel", "supervisor", 38000, 9),

  // ----- CORTES VIRAIS -----
  P("pablomarcal","Pablo Marçal",
    "Mestre dos cortes virais. Lê o algoritmo, gera dependência. Escala via dor + dopamina.",
    "social:cortes", "supervisor", 45000, 10),

  // ============ PROGRAMAÇÃO ============
  P("linus",   "Linus Torvalds",
    "Kernel Linux + Git. Brutal em code review. Resolve bug que ninguém vê.",
    "programacao:senior", "supervisor", 70000, 10),
  P("carmack", "John Carmack",
    "Doom, Quake, motor 3D. Otimização extrema, primeiros princípios.",
    "programacao:senior", "supervisor", 65000, 10),
  P("dhh",     "DHH (David Heinemeier Hansson)",
    "Ruby on Rails. Convention over configuration. Opiniões fortes, código curto.",
    "programacao:senior", "supervisor", 50000, 9),

  P("jr-dev-1","Equipe VPC Dev #1",
    "Dev júnior interno. Pega o que tá no backlog, manda ver.",
    "programacao:junior", "especialista", 5000, 5),
  P("jr-dev-2","Equipe VPC Dev #2",
    "Dev júnior interno. Foco em testes, automação, suporte ao senior.",
    "programacao:junior", "especialista", 5000, 5),
  P("jr-dev-3","Equipe VPC Dev #3",
    "Dev júnior interno. Bug fix, manutenção das ferramentas internas.",
    "programacao:junior", "especialista", 5000, 5),

  P("theo",    "Theo (t3.gg)",
    "Vibe coder. T3 stack, Next.js, opinionated. Faz produto saindo do nada.",
    "programacao:vibe", "especialista", 18000, 8),
  P("levels",  "Pieter Levels",
    "Indie hacker máximo. PHP em arquivo único, lança em 24h, fatura milhões.",
    "programacao:vibe", "especialista", 22000, 9),

  // ============ ENDOMARKETING ============
  P("chespirito", "Chespirito (Xispirito)",
    "Roberto Gómez Bolaños — gênio cômico, criador de mundos. Eleva o clima do time.",
    "endomarketing:dir", "supervisor", 35000, 9),

  P("chaves",     "Chaves",
    "Foi sem querer querendo. Humor inocente que une qualquer equipe.",
    "endomarketing:humor", "especialista", 18000, 7),

  P("ceara",      "Matheus Ceará",
    "Piada curta na rajada. Quebra qualquer tensão em 5 segundos.",
    "endomarketing:humor", "especialista", 22000, 8),

  P("paulinhogogo","Paulinho Gogó",
    "Convidado esporádico. 'NÃO ACREDITO, CARLOS ALBERTO!'. Vem só em ocasiões especiais.",
    "endomarketing:convidado", "especialista", 12000, 8)
];

export const slotOf = (deptId: string, roleId: string) => `${deptId}:${roleId}`;
